import {createSelector} from '@reduxjs/toolkit';
import {RootState} from '../store';
import {decodeCensorMeClaims} from '../../lib/jwtClaims';
import {appUserSelector, sessionSelector} from './auth.selectors';
import {AdminSubscriptionPlan, AdminUser} from '../../domain/admin';
import {AdminWordlist} from '../../domain/adminWordlists';

const adminStateSelector = (state: RootState) => state.admin;

/* ==========================================================================
 * Is this account an admin?
 * ========================================================================== */

/**
 * A **hint**, and named one so nothing mistakes it for authorization.
 *
 * `censor_me_is_admin` is stamped into the access token when it is issued, so it keeps saying true
 * for the life of a session after the flag is revoked in the database, and it is absent entirely
 * from tokens issued before the claim existed. It is good for exactly one thing: deciding whether
 * to draw the admin link in the header, so the other 99.9% of users never see a door they cannot
 * open. The server re-reads the flag on every request and answers a non-admin with 404 — that is
 * the only thing that decides anything.
 */
export const isAdminHintSelector = createSelector(
  [sessionSelector, appUserSelector],
  (session, appUser): boolean => {
    if (typeof appUser?.is_admin === 'boolean' && appUser.is_admin) {
      return true;
    }
    return decodeCensorMeClaims(session?.access_token)?.censor_me_is_admin === true;
  }
);

/** True once the server has answered any admin request with 404. Authorization, unlike the hint. */
export const adminAccessDeniedSelector = (state: RootState) => adminStateSelector(state).accessDenied;

/* ==========================================================================
 * Request state
 * ========================================================================== */

export const adminStatsApiSelector = (state: RootState) => adminStateSelector(state).api.stats;
export const adminUsersApiSelector = (state: RootState) => adminStateSelector(state).api.users;
export const adminUserDetailApiSelector = (state: RootState) => adminStateSelector(state).api.userDetail;
export const adminUserTranscriptionsApiSelector = (state: RootState) =>
  adminStateSelector(state).api.userTranscriptions;
export const adminUserConversionsApiSelector = (state: RootState) =>
  adminStateSelector(state).api.userConversions;
export const adminUserEmailsApiSelector = (state: RootState) => adminStateSelector(state).api.userEmails;
export const adminResendSetupEmailApiSelector = (state: RootState) =>
  adminStateSelector(state).api.resendSetupEmail;
export const adminSignupsApiSelector = (state: RootState) => adminStateSelector(state).api.signups;
export const adminEmailsApiSelector = (state: RootState) => adminStateSelector(state).api.emails;
export const adminSuppressionsApiSelector = (state: RootState) =>
  adminStateSelector(state).api.suppressions;
export const adminTranscriptionQueueApiSelector = (state: RootState) =>
  adminStateSelector(state).api.transcriptionQueue;
export const adminWordlistsApiSelector = (state: RootState) => adminStateSelector(state).api.wordlists;
export const adminWordlistDetailApiSelector = (state: RootState) =>
  adminStateSelector(state).api.wordlistDetail;
export const adminWordlistCreateApiSelector = (state: RootState) =>
  adminStateSelector(state).api.wordlistCreate;
export const adminWordlistSaveApiSelector = (state: RootState) => adminStateSelector(state).api.wordlistSave;
export const adminWordlistDeleteApiSelector = (state: RootState) =>
  adminStateSelector(state).api.wordlistDelete;

/* ==========================================================================
 * Data
 * ========================================================================== */

export const adminStatsSelector = (state: RootState) => adminStateSelector(state).data.stats;
export const adminUsersPageSelector = (state: RootState) => adminStateSelector(state).data.users;
export const adminUserDetailSelector = (state: RootState) => adminStateSelector(state).data.userDetail;
export const adminUserTranscriptionsPageSelector = (state: RootState) =>
  adminStateSelector(state).data.userTranscriptions;
export const adminUserConversionsPageSelector = (state: RootState) =>
  adminStateSelector(state).data.userConversions;
export const adminUserEmailsPageSelector = (state: RootState) => adminStateSelector(state).data.userEmails;
export const adminSignupsPageSelector = (state: RootState) => adminStateSelector(state).data.signups;
export const adminEmailsPageSelector = (state: RootState) => adminStateSelector(state).data.emails;
export const adminSuppressionsPageSelector = (state: RootState) =>
  adminStateSelector(state).data.suppressions;
export const adminTranscriptionQueuePageSelector = (state: RootState) =>
  adminStateSelector(state).data.transcriptionQueue;
export const adminWordlistsPageSelector = (state: RootState) => adminStateSelector(state).data.wordlists;
export const adminWordlistDetailSelector = (state: RootState) =>
  adminStateSelector(state).data.wordlistDetail;

export const adminUsersSelector = createSelector(
  [adminUsersPageSelector],
  (page): AdminUser[] => page?.items ?? []
);

export const adminUsersTotalSelector = createSelector(
  [adminUsersPageSelector],
  page => page?.total ?? 0
);

export const adminWordlistsSelector = createSelector(
  [adminWordlistsPageSelector],
  (page): AdminWordlist[] => page?.items ?? []
);

export const adminWordlistsTotalSelector = createSelector(
  [adminWordlistsPageSelector],
  page => page?.total ?? 0
);

export const adminSignupsSelector = createSelector(
  [adminSignupsPageSelector],
  page => page?.items ?? []
);

export const adminSignupsTotalSelector = createSelector(
  [adminSignupsPageSelector],
  page => page?.total ?? 0
);

/* ==========================================================================
 * Derived
 * ========================================================================== */

/**
 * The dashboard's alert row: the tiles that mean somebody is stuck, as opposed to the tiles that
 * are just numbers. `signupsFailed` is the loud one — every count there is a person who has been
 * charged and cannot sign in.
 */
export interface AdminAlerts {
  signupsFailed: number;
  emailsUndelivered30d: number;
  /**
   * Addresses Resend refuses outright. Distinct from the 30-day failure count: that is individual
   * sends, this is the standing blocklist behind them, and a suppressed address silently swallows
   * every future send including the setup email.
   */
  suppressionsActive: number;
  hasAlerts: boolean;
}

export const adminAlertsSelector = createSelector(
  [adminStatsSelector],
  (stats): AdminAlerts => ({
    signupsFailed: stats?.signupsFailed ?? 0,
    emailsUndelivered30d: stats?.emailsUndelivered30d ?? 0,
    suppressionsActive: stats?.suppressionsActive ?? 0,
    hasAlerts: (stats?.signupsFailed ?? 0) > 0 || (stats?.suppressionsActive ?? 0) > 0,
  })
);

/**
 * How a user's subscription should be badged: off the **derived** fields, never the Paddle mirror.
 * A lapsed Pro carries `subscriptionPlan: 'pro'` with `effectiveSubscriptionPlan: 'free'`, and
 * showing the first one is the specific bug the contract's two-field split exists to prevent.
 */
export interface SubscriptionBadge {
  label: string;
  plan: AdminSubscriptionPlan;
  isActive: boolean;
  /** True when the raw mirror and the derived truth disagree — worth showing on the detail page. */
  mirrorDisagrees: boolean;
}

export const describeSubscription = (user: {
  subscribed: boolean;
  subscriptionPlan: AdminSubscriptionPlan;
  isSubscriptionActive: boolean;
  effectiveSubscriptionPlan: AdminSubscriptionPlan;
}): SubscriptionBadge => ({
  label: user.isSubscriptionActive ? planLabel(user.effectiveSubscriptionPlan) : 'Lapsed',
  plan: user.effectiveSubscriptionPlan,
  isActive: user.isSubscriptionActive,
  mirrorDisagrees:
    user.subscribed !== user.isSubscriptionActive
    || user.subscriptionPlan !== user.effectiveSubscriptionPlan,
});

export const planLabel = (plan: AdminSubscriptionPlan): string => {
  switch (plan) {
    case 'pro':
      return 'Pro';
    case 'team':
      return 'Team';
    default:
      return 'Free';
  }
};
